import { useState, useEffect } from 'react';
import InputField from '../../../components/Forms/Input/InputField';
import SimpleSearchSelect from '../../../components/Forms/Input/SimpleSearchSelect';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import { calculateTaxValues } from '../../../utils/taxCalculator';
import { useDebitNoteLookup } from '../../../hooks/purchases/useDebitNoteLookup';
import SvgIcon from '../../SvgIcon/SvgIcon';
import { useDebitNote } from '../../../hooks/purchases/useDebitNote';

interface Props {
  onAdd: (product: any) => void;
}

const AddPriceAdjustmentForm = ({ onAdd }: Props) => {
  const {
    form: { supplier, id: debitNoteId },
  } = useSelector((state: RootState) => state.basePurchase);

  const { handleCancelPriceAdjustment } = useDebitNote();

  const {
    filteredDocuments,
    productOptions,
    searchEligiblePurchases,
    fetchProductsByPurchase,
    clearDebitNoteLookup,
  } = useDebitNoteLookup();

  const [form, setForm] = useState({
    purchaseRefId: '',
    productId: '',
    productName: '',
    quantity: '', // ⚠️ Solo lectura
    newUnitCost: '',
    ivaTotal: '',
    totalCost: '',
    withIva: '',
    ivaUnit: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (supplier?.id) {
      searchEligiblePurchases({ supplierId: supplier.id });
    }
    return () => clearDebitNoteLookup();
  }, [supplier?.id]);

  useEffect(() => {
    const { newUnitCost, quantity } = form;
    if (newUnitCost && quantity && parseFloat(quantity) > 0) {
      const calc = calculateTaxValues(
        parseFloat(newUnitCost),
        parseFloat(quantity)
      );
      setForm((prev) => ({
        ...prev,
        ivaTotal: calc.ivaTotal.toString(),
        totalCost: calc.totalCost.toString(),
        withIva: calc.unitCostWithIva.toString(),
        ivaUnit: calc.ivaUnit.toString(),
      }));
    }
  }, [form.newUnitCost, form.quantity]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleAdd = () => {
    const errs: typeof errors = {};
    if (!form.purchaseRefId) errs.purchaseRefId = 'CCF requerido';
    if (!form.productId) errs.productId = 'Producto requerido';
    if (!form.newUnitCost) errs.newUnitCost = 'Nuevo costo requerido';
    // ❌ NO validar cantidad aquí

    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }

    const payload = {
      debitNoteId,
      purchaseReferenceId: Number(form.purchaseRefId),
      productId: Number(form.productId),
      quantity: Number(form.quantity),
      unitCost: Number(form.newUnitCost),
      type: 'PRICE_INCREASE',
    };

    onAdd(payload);
  };

  const resetForm = () => {
    setForm({
      purchaseRefId: '',
      productId: '',
      productName: '',
      newUnitCost: '',
      quantity: '',
      ivaTotal: '',
      totalCost: '',
      withIva: '',
      ivaUnit: '',
    });
    setErrors({});
  };

  useEffect(() => {
    return () => {
      resetForm();
    };
  }, []);

  return (
    <div className="space-y-4">
      {filteredDocuments.length === 0 && (
        <div className="flex items-start gap-2 rounded border border-yellow-300 bg-yellow-50 p-3 text-yellow-700">
          <SvgIcon name="info" className="mt-0.5 h-5 w-5 text-yellow-500" />
          <p className="text-sm">
            Este proveedor no tiene compras{' '}
            <strong>de tipo Crédito Fiscal</strong> disponibles para aplicar una{' '}
            <strong>nota de débito</strong>.
          </p>
        </div>
      )}

      <SimpleSearchSelect
        label="CCF del Proveedor"
        name="purchaseRefId"
        iconKey="invoice"
        options={filteredDocuments}
        selectedValue={form.purchaseRefId ? Number(form.purchaseRefId) : null}
        onSelect={(option: any) => {
          setForm((prev) => ({
            ...prev,
            purchaseRefId: option.id,
            productId: '',
            productName: '',
            newUnitCost: '',
            quantity: '',
          }));
          fetchProductsByPurchase(option.id);
        }}
        error={errors.purchaseRefId}
        required
      />

      <SimpleSearchSelect
        label="Producto del CCF que Afecta"
        name="productId"
        iconKey="product"
        options={productOptions}
        selectedValue={form.productId ? Number(form.productId) : null}
        onSelect={(option: any) => {
          setForm((prev) => ({
            ...prev,
            productId: option.id,
            productName: option.name,
            quantity: option.quantity?.toString() || '1', // ← precargar cantidad
          }));
        }}
        error={errors.productId}
        required
      />

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <InputField
          label="Cantidad (facturada en el CCF)"
          name="quantity"
          type="number"
          iconKey="product"
          value={form.quantity}
          onChange={() => {}}
          disabled
        />

        <InputField
          label="Nuevo Costo Unitario"
          name="newUnitCost"
          type="number"
          iconKey="dollar"
          value={form.newUnitCost}
          onChange={handleChange}
          error={errors.newUnitCost}
          required
        />
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3">
        <InputField
          label="IVA Total"
          name="ivaTotal"
          type="number"
          iconKey="dollar"
          value={form.ivaTotal}
          onChange={() => {}}
          disabled
        />
        <InputField
          label="Costo Total"
          name="totalCost"
          type="number"
          iconKey="dollar"
          value={form.totalCost}
          onChange={() => {}}
          disabled
        />
      </div>

      <div className="text-gray-600 space-y-1 text-sm dark:text-white">
        <p>CON IVA: $ {form.withIva || '0.00000000'}</p>
        <p>IVA UNITARIO: $ {form.ivaUnit || '0.00000000'}</p>
      </div>

      <div className="flex justify-end gap-3 pt-4">
        <button
          type="button"
          onClick={handleAdd}
          className="inline-flex items-center gap-2 rounded-md bg-primary px-5 py-2.5 text-sm font-medium text-white shadow-sm transition hover:bg-opacity-90"
        >
          <SvgIcon name="save" className="h-5 w-5" />
          Guardar
        </button>
        <button
          type="button"
          onClick={handleCancelPriceAdjustment}
          className="inline-flex items-center gap-2 rounded-md bg-slate-500 px-5 py-2.5 text-sm font-medium text-white shadow-sm transition hover:bg-opacity-90"
        >
          <SvgIcon name="cancel" className="h-5 w-5" />
          Cancelar
        </button>
      </div>
    </div>
  );
};

export default AddPriceAdjustmentForm;
